//
// Copyright (c) 2025 Comcast
//

import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router'
import { useSelector } from 'react-redux'
import forIn from 'lodash/forIn'
import isEqual from 'lodash/isEqual'

import * as sessionUserSelector from 'selectors/sessionUser'

import { useFetchSessionUserQuery } from 'api/sessionUser'
import {
  useFetchTicketForEditQuery,
  useUpdateTicketMutation,
} from 'api/tickets'

import { RouteTicketingSystems, RouteTicketingSystemsTickets, RouteTicketingSystemsTicket } from 'routes'
import { getInitialValues } from 'utils/ticketFields'
import * as ticketSelector from 'selectors/ticket'

import ErrorPage404 from 'components/App/ErrorPage/ErrorPage404'
import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import Header from 'components/Shared/Header/Index'
import TicketForm from 'components/App/TicketEditPage/TicketForm/Index'
import TicketPageSidebar from 'components/App/TicketPage/TicketPageSidebar/Index'
import ConfirmDialogBody from './ConfirmDialogBody'

import { confirm } from 'components/Shared/ConfirmDialog/Index'

const TicketEditPage = () => {
  // Polling for avoid session ending
  useFetchSessionUserQuery({}, { pollingInterval: 60000 * 10 })


  const { t } = useTranslation()
  const params = useParams()
  const navigate = useNavigate()
  const isUpdating = useSelector(ticketSelector.isUpdating)
  const userLogin = useSelector(sessionUserSelector.login)

  const ticketingSystemDisplayId = params.ticketingSystemDisplayId || null
  const ticketId = params.ticketId || null

  const { data, isFetching } = useFetchTicketForEditQuery({
    ticketingSystemDisplayId: ticketingSystemDisplayId,
    ticketId: ticketId,
  })

  const [updateTicket, { isLoading: isUpdatingTicket }] = useUpdateTicketMutation()

  const ticket = data?.ticket || {}
  const ticketingSystemName = ticket.ticketingSystemName
  const parentTicket = ticket.parentTicket || null
  const withHierarchy = !!ticket.hierarchyEnabled

  const ticketRequiredCommentsFields = ticket?.bodyFields
    ? ticket.bodyFields.filter(field => field.commentRequired).map(field => field.name)
    : []

  const onTicketUpdate = (values) => {
    const paramValues = {}
    const requiredCommentsParamsNames = []

    forIn(values, (value, name) => {
      // TODO: remove when bug with arrays inside partner_ticket_id is fixed
      const isPartnerIdArray = name === 'partner_ticket_id' && value?.[0] === '['

      if (!isEqual(value, initialValues[name]) && !isPartnerIdArray) {
        if (value._isAMomentObject) {
          paramValues[name] = value.unix()
        } else if (withHierarchy && name === 'parent') {
          paramValues[name] = value.shortLabel || value.value
        } else {
          paramValues[name] = value
        }
        if (ticketRequiredCommentsFields.includes(name)) {
          requiredCommentsParamsNames.push(name)
        }
      }
    })

    const params = {
      ticket: paramValues,
    }

    const confirmBodyComponent = (props) => (
      <ConfirmDialogBody {...props} />
    )

    if (requiredCommentsParamsNames.length) {
      confirm({
        title: t('common.confirmChanges'),
        confirmLabel: t('common.confirm'),
        cancelLabel: t('common.cancel'),
        bodyComponent: confirmBodyComponent,
        showJustification: true,
        bodyComponentProps: { values: requiredCommentsParamsNames },
      }).then(
        ({ justification }) => {
          params.ticket.comments = [{ body: justification, author: userLogin }]
          handleUpdateTicket(params)
        },
        () => {}
      )
    } else {
      handleUpdateTicket(params)
    }
  }

  const handleUpdateTicket = (params) => {
    updateTicket({
      ticketingSystemDisplayId: ticketingSystemDisplayId,
      ticketId: ticketId,
      ticketParams: params,
    }).then(fulfilled => {
      if (!fulfilled.data?.error) {
        // switch to updated ticket
        navigate(RouteTicketingSystemsTicket({ ticketingSystemDisplayId, ticketId }))
      }
    })
  }

  const onCancel = () => {
    navigate(RouteTicketingSystemsTicket({ ticketingSystemDisplayId, ticketId }))
  }

  const getBreadcrumbs = () => {
    const ticketingSystemsUrl = RouteTicketingSystems()
    const ticketingSystemUrl = RouteTicketingSystemsTickets({ ticketingSystemDisplayId })
    const ticketUrl = RouteTicketingSystemsTicket({ ticketingSystemDisplayId, ticketId })

    const breadcrumbsList = [{
      text: t('common.breadcrumbTicketingSystems'),
      url: ticketingSystemsUrl,
    }, {
      text: ticketingSystemName,
      url: ticketingSystemUrl,
    }, {
      text: ticket.externalId,
      url: ticketUrl,
    }, {
      text: t('common.breadcrumbTicketEdit'),
    }]
    return breadcrumbsList
  }

  // canBeEdited will be undefined if ticketing system was not found so we may avoid
  // checking the 'Ticket not found' message. It will show 404 page in this case anyway
  if (data && !ticket.canBeEdited) {
    return <ErrorPage404 />
  }

  const titleParams = {
    'ticketName': ticket.externalId,
    'tsName': ticketingSystemName,
    interpolation: { escapeValue: false },
  }
  const title = t('ticketEditPage.pageHeader', titleParams)
  const isLoading = isFetching || isUpdatingTicket || isUpdating
  const editableFields = ticket.bodyFields || []
  const initialValues = getInitialValues(editableFields, parentTicket)

  return (
    <MainWrap pageTitle={title}>
      <TicketPageSidebar ticket={ticket} />
      <Content loading={isLoading}>
        <Header
          title={title}
          breadcrumbsList={getBreadcrumbs()}
        />
        <TicketForm
          isEdit
          fields={editableFields}
          initialValues={initialValues}
          ticketId={ticketId}
          ticketingSystemDisplayId={ticketingSystemDisplayId}
          onSubmit={onTicketUpdate}
          onCancel={onCancel}
          withHierarchy={withHierarchy}
          parentTicket={parentTicket}
        />
      </Content>
    </MainWrap>
  )
}

export default TicketEditPage
